// ============================================================================
// Rondines — cliente de la API (módulo `patrols`, CC-CONSOLE #1592). El estado
// y el % de cumplimiento de una ronda los calcula el servidor.
// ============================================================================

import api, { apiFormData } from "../../../utils/api";

export const CHECKPOINT_METHODS = ["qr", "nfc", "gps"] as const;
export type CheckpointMethod = (typeof CHECKPOINT_METHODS)[number];
export const SHIFTS = ["matutino", "vespertino", "nocturno"] as const;
export type Shift = (typeof SHIFTS)[number];
export const ROUND_STATUSES = ["completed", "partial", "missed", "in_progress", "scheduled"] as const;
export type RoundStatus = (typeof ROUND_STATUSES)[number];
export const SEVERITIES = ["low", "medium", "high", "critical"] as const;
export type Severity = (typeof SEVERITIES)[number];
export const INCIDENT_STATUSES = ["open", "in_progress", "resolved", "closed"] as const;
export type IncidentStatus = (typeof INCIDENT_STATUSES)[number];
export const INCIDENT_CATEGORIES = ["intrusion", "damage", "lighting", "leak", "door", "fire", "safety", "other"] as const;
export type IncidentCategory = (typeof INCIDENT_CATEGORIES)[number];

export interface PersonRef {
  id: string;
  name: string;
}

export interface PatrolRoute {
  id: string;
  name: string;
  base_id: string;
  baseName?: string;
  description?: string;
  zone?: string;
  color?: string;
  status: "active" | "inactive";
  estimatedMinutes?: number;
  checkpointCount?: number;
}

export interface Checkpoint {
  id: string;
  route_id: string;
  base_id?: string;
  name: string;
  order: number;
  method: CheckpointMethod;
  code?: string;
  latitude?: number | null;
  longitude?: number | null;
  radiusM?: number;
  photo?: string | null;
  instructions?: string;
  zone?: string;
  required?: boolean;
}

export interface PatrolSchedule {
  id: string;
  route_id: string;
  routeName?: string;
  base_id?: string;
  name: string;
  shift: Shift;
  days: string[];
  startTime: string;
  endTime: string;
  everyMinutes: number;
  toleranceMinutes?: number;
  guards: PersonRef[];
  status: "active" | "paused";
  times: string[];
}

export interface RoundCheck {
  checkpoint_id: string;
  name: string;
  order: number;
  method?: CheckpointMethod;
  status: "pending" | "done" | "skipped";
  at: string | null;
  note?: string;
}

export interface PatrolRound {
  id: string;
  route_id: string;
  routeName?: string;
  base_id?: string;
  schedule_id?: string | null;
  shift?: Shift;
  guard?: PersonRef | null;
  plannedStart: string;
  plannedEnd?: string | null;
  actualStart?: string | null;
  actualEnd?: string | null;
  status: RoundStatus;
  checkpoints: RoundCheck[];
  totalCheckpoints: number;
  doneCheckpoints: number;
  skippedCheckpoints: number;
  compliancePct: number;
  delayMinutes?: number | null;
  durationMinutes?: number | null;
  notes?: string;
}

export interface PatrolIncident {
  id: string;
  title: string;
  description?: string;
  category: IncidentCategory;
  severity: Severity;
  status: IncidentStatus;
  base_id: string;
  baseName?: string;
  route_id?: string | null;
  routeName?: string;
  round_id?: string | null;
  checkpoint_id?: string | null;
  checkpointName?: string;
  photos: string[];
  reportedBy?: PersonRef | null;
  assignee?: PersonRef | null;
  reportedAt: string;
  resolvedAt?: string | null;
  resolution?: string;
}

export interface Tally {
  total: number;
  completed: number;
  partial: number;
  missed: number;
  inProgress: number;
  scheduled: number;
  compliancePct: number | null;
}

export interface PatrolDashboard {
  routes: number;
  activeRoutes: number;
  checkpoints: number;
  schedules: number;
  guards: PersonRef[];
  today: Tally;
  week: Tally;
  days: Array<Tally & { date: string }>;
  byRoute: Array<{ routeId: string; name: string; baseName: string; color: string | null; rounds: number; compliancePct: number | null }>;
  mostSkipped: Array<{ checkpointId: string; name: string; routeId: string | null; skipped: number }>;
  incidents: { open: number; bySeverity: Record<Severity, number>; recent: PatrolIncident[] };
}

const listOf = <T>(body: unknown): T[] => {
  const data = (body as { data?: unknown })?.data;
  return Array.isArray(data) ? (data as T[]) : [];
};
const docOf = <T>(body: unknown): T => (body as { data: T }).data;
type Draft<T> = Partial<Omit<T, "id">>;
const clean = (p: Record<string, string | undefined>) =>
  Object.fromEntries(Object.entries(p).filter(([, v]) => v !== undefined && v !== "")) as Record<string, string>;

export const getDashboard = async (baseId?: string) =>
  docOf<PatrolDashboard>((await api.get("/patrols/dashboard", { params: clean({ baseId }) })).data);

export const listRoutes = async (baseId?: string) =>
  listOf<PatrolRoute>((await api.get("/patrols/routesList", { params: clean({ baseId }) })).data);
export const createRoute = async (d: Draft<PatrolRoute>) => docOf<PatrolRoute>((await api.post("/patrols/route", d)).data);
export const updateRoute = async (id: string, d: Draft<PatrolRoute>) =>
  docOf<PatrolRoute>((await api.put("/patrols/route", d, { params: { id } })).data);
export const deleteRoute = async (id: string) => {
  await api.delete("/patrols/route", { params: { id } });
};

export const listCheckpoints = async (routeId?: string) =>
  listOf<Checkpoint>((await api.get("/patrols/checkpointsList", { params: clean({ routeId }) })).data);

function checkpointForm(d: Draft<Checkpoint>, file?: File | null): FormData {
  const form = new FormData();
  for (const [k, v] of Object.entries(d)) if (v !== undefined && v !== null) form.append(k, String(v));
  if (file) form.append("photo0", file);
  return form;
}
export const createCheckpoint = async (d: Draft<Checkpoint>, file?: File | null) =>
  docOf<Checkpoint>((await apiFormData.post("/patrols/checkpoint", checkpointForm(d, file))).data);
export const updateCheckpoint = async (id: string, d: Draft<Checkpoint>, file?: File | null) =>
  docOf<Checkpoint>((await apiFormData.put("/patrols/checkpoint", checkpointForm(d, file), { params: { id } })).data);
export const deleteCheckpoint = async (id: string) => {
  await api.delete("/patrols/checkpoint", { params: { id } });
};

export const listSchedules = async (routeId?: string) =>
  listOf<PatrolSchedule>((await api.get("/patrols/schedulesList", { params: clean({ routeId }) })).data);
export const createSchedule = async (d: Draft<PatrolSchedule>) => docOf<PatrolSchedule>((await api.post("/patrols/schedule", d)).data);
export const updateSchedule = async (id: string, d: Draft<PatrolSchedule>) =>
  docOf<PatrolSchedule>((await api.put("/patrols/schedule", d, { params: { id } })).data);
export const deleteSchedule = async (id: string) => {
  await api.delete("/patrols/schedule", { params: { id } });
};

export const listRounds = async (params: { routeId?: string; baseId?: string; status?: string; from?: string; to?: string } = {}) =>
  listOf<PatrolRound>((await api.get("/patrols/roundsList", { params: clean(params) })).data);
export const createRound = async (d: Record<string, unknown>) => docOf<PatrolRound>((await api.post("/patrols/round", d)).data);
export const updateRound = async (id: string, d: Record<string, unknown>) =>
  docOf<PatrolRound>((await api.put("/patrols/round", d, { params: { id } })).data);

export const listIncidents = async (params: { baseId?: string; status?: string; severity?: string } = {}) =>
  listOf<PatrolIncident>((await api.get("/patrols/incidentsList", { params: clean(params) })).data);

function incidentForm(d: Draft<PatrolIncident>, files: File[]): FormData {
  const form = new FormData();
  for (const [k, v] of Object.entries(d)) {
    if (v === undefined || v === null || k === "photos") continue;
    form.append(k, typeof v === "object" ? JSON.stringify(v) : String(v));
  }
  if (d.photos !== undefined || files.length) form.append("existingPhotos", JSON.stringify(d.photos ?? []));
  files.forEach((f, i) => form.append(`photo${i}`, f));
  return form;
}
export const createIncident = async (d: Draft<PatrolIncident>, files: File[] = []) =>
  docOf<PatrolIncident>((await apiFormData.post("/patrols/incident", incidentForm(d, files))).data);
export const updateIncident = async (id: string, d: Draft<PatrolIncident>, files: File[] = []) =>
  docOf<PatrolIncident>((await apiFormData.put("/patrols/incident", incidentForm(d, files), { params: { id } })).data);
export const deleteIncident = async (id: string) => {
  await api.delete("/patrols/incident", { params: { id } });
};
